import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

interface SidebarContextType {
  isCollapsed: boolean;
  toggleCollapse: () => void;
  setIsCollapsed: (value: boolean) => void;
  isMobile: boolean;
}

const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

export const useSidebar = () => {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error('useSidebar must be used within SidebarProvider');
  }
  return context;
};

const MOBILE_BREAKPOINT = 768; // md breakpoint — below this is phone-style layout
const TABLET_BREAKPOINT = 1024; // lg breakpoint — between md and lg we default to collapsed
const STORAGE_KEY = 'sidebar-collapsed';

export const SidebarProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Detect if we're on mobile (phone-style, no sidebar at all)
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth < MOBILE_BREAKPOINT;
  });

  // Initialize collapsed state based on screen size and stored preference
  const [isCollapsed, setIsCollapsed] = useState(() => {
    if (typeof window === 'undefined') return true;
    
    // On phones, sidebar is hidden — collapsed value doesn't really matter
    if (window.innerWidth < MOBILE_BREAKPOINT) {
      return true;
    }

    // On tablet-width viewports (md to lg), default to collapsed so the
    // narrow content area has breathing room. Honour stored preference if set.
    if (window.innerWidth < TABLET_BREAKPOINT) {
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored !== null) {
          return JSON.parse(stored);
        }
      } catch {
        // Ignore storage errors
      }
      return true;
    }
    
    // On desktop, check localStorage for user preference
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored !== null) {
        return JSON.parse(stored);
      }
    } catch {
      // Ignore storage errors
    }
    
    // Default to expanded on desktop
    return false;
  });

  // Handle window resize with debounce
  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;
    
    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        const width = window.innerWidth;
        const mobile = width < MOBILE_BREAKPOINT;
        setIsMobile(mobile);
        
        if (mobile) {
          // Phone widths: sidebar is hidden via CSS; keep state collapsed
          setIsCollapsed(true);
        } else if (width < TABLET_BREAKPOINT) {
          // Tablet widths: honour stored preference, otherwise collapse
          try {
            const stored = localStorage.getItem(STORAGE_KEY);
            setIsCollapsed(stored !== null ? JSON.parse(stored) : true);
          } catch {
            setIsCollapsed(true);
          }
        } else {
          // Desktop widths: restore user preference, default expanded
          try {
            const stored = localStorage.getItem(STORAGE_KEY);
            if (stored !== null) {
              setIsCollapsed(JSON.parse(stored));
            } else {
              setIsCollapsed(false);
            }
          } catch {
            setIsCollapsed(false);
          }
        }
      }, 100);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(timeoutId);
    };
  }, []);

  // Toggle collapse and persist preference (desktop only)
  const toggleCollapse = useCallback(() => {
    setIsCollapsed(prev => {
      const newState = !prev;
      
      // Only persist preference on desktop
      if (!isMobile) {
        try {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(newState));
        } catch {
          // Ignore storage errors
        }
      }
      
      return newState;
    });
  }, [isMobile]);

  // Custom setIsCollapsed that also persists on desktop
  const handleSetIsCollapsed = useCallback((value: boolean) => {
    setIsCollapsed(value);
    
    if (!isMobile) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
      } catch {
        // Ignore storage errors
      }
    }
  }, [isMobile]);

  return (
    <SidebarContext.Provider 
      value={{ 
        isCollapsed, 
        toggleCollapse, 
        setIsCollapsed: handleSetIsCollapsed,
        isMobile 
      }}
    >
      {children}
    </SidebarContext.Provider>
  );
};
